import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axiosClient from '../../services/axiosClient';
import swal from 'sweetalert';

// Fonction utilitaire pour lire le token du localStorage
function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const { token, tokenType } = JSON.parse(str) || {};
		if (token) {
			return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		}
		return {};
	} catch (_) {
		return {};
	}
}

const JobTypes = () => {
	const [postTypes, setPostTypes] = useState([]);
	const [filteredPostTypes, setFilteredPostTypes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedPostTypes, setSelectedPostTypes] = useState([]);
	const [showAddModal, setShowAddModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [currentPostType, setCurrentPostType] = useState(null);
	const [recordsPerPage, setRecordsPerPage] = useState(25);
	const [formData, setFormData] = useState({
		name: '',
		active: 1
	});

	// Fetch post types from API
	useEffect(() => {
		fetchPostTypes();
	}, []);

	const fetchPostTypes = async () => {
		try {
			setLoading(true);
			console.log('[JobTypes] Fetching from /admin/postTypes...');
			const response = await axiosClient.get('/admin/postTypes', {
				headers: { 
					'Accept': 'application/json',
					...getAuthHeader() 
				},
			});
			console.log('[JobTypes] API Response:', response);
			
			let data = response?.data;
			let list = data?.result?.data || data?.data || data?.result || data || [];
			if (!Array.isArray(list)) {
				if (list && Array.isArray(list.data)) list = list.data;
				else list = [];
			}
			
			// Normaliser les données
			const normalizedPostTypes = list.map(pt => {
				// Gérer le cas où name est un objet de traductions
				let displayName = pt.name || pt.title || pt.label;
				if (displayName && typeof displayName === 'object') {
					// Essayer d'extraire la traduction française, puis anglaise, puis la première disponible
					displayName = displayName.fr || displayName.en || displayName[Object.keys(displayName)[0]] || 'Sans nom';
				}
				
				return {
					id: pt.id || pt.post_type_id,
					name: String(displayName || 'Sans nom'),
					active: pt.active !== undefined ? pt.active : 1,
					translation: pt.translation,
					rawName: pt.name // Garder l'objet original pour l'édition
				};
			});
			
			console.log('[JobTypes] Parsed post types:', normalizedPostTypes);
			setPostTypes(normalizedPostTypes);
			setFilteredPostTypes(normalizedPostTypes);
		} catch (err) {
			console.error('[JobTypes] Error fetching post types:', err);
		} finally {
			setLoading(false);
		}
	};

	// Filter post types based on search
	useEffect(() => {
		let filtered = [...postTypes];

		if (searchTerm) {
			filtered = filtered.filter(pt =>
				pt.name?.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		setFilteredPostTypes(filtered);
	}, [searchTerm, postTypes]);

	// Handlers
	const handleAddPostType = async () => {
		try {
			const response = await axiosClient.post('/admin/postTypes', formData, {
				headers: { 
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					...getAuthHeader() 
				},
			});
			
			if (response.status === 200 || response.status === 201) {
				await fetchPostTypes();
				setShowAddModal(false);
				setFormData({ name: '', active: 1 });
			}
		} catch (err) {
			console.error('Error adding post type:', err);
			alert('Erreur lors de l\'ajout du type d\'annonce');
		}
	};

	const handleEditPostType = (postType) => {
		setCurrentPostType(postType);
		setFormData({
			name: postType.name,
			active: postType.active
		});
		setShowEditModal(true);
	};

	const handleUpdatePostType = async () => {
		try {
			const response = await axiosClient.put(
				`/admin/postTypes/${currentPostType.id}`, 
				formData,
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200) {
				await fetchPostTypes();
				setShowEditModal(false);
				setCurrentPostType(null);
				setFormData({ name: '', active: 1 });
			}
		} catch (err) {
			console.error('Error updating post type:', err);
			alert('Erreur lors de la mise à jour du type d\'annonce');
		}
	};

	const handleDeletePostType = async (id) => {
		const willDelete = await swal({
			title: 'Êtes-vous sûr ?',
			text: 'Cette action supprimera définitivement ce type d\'annonce. Cette action est irréversible !',
			icon: 'warning',
			buttons: {
				cancel: {
					text: 'Annuler',
					value: false,
					visible: true,
					className: 'btn-secondary'
				},
				confirm: {
					text: 'Oui, supprimer',
					value: true,
					visible: true,
					className: 'btn-danger'
				}
			},
			dangerMode: true,
		});

		if (!willDelete) return;
		
		try {
			const response = await axiosClient.delete(`/admin/postTypes/${id}`, {
				headers: { 
					'Accept': 'application/json',
					...getAuthHeader() 
				},
			});
			
			if (response.status === 200 || response.status === 204) {
				await fetchPostTypes();
				swal('Supprimé !', 'Le type d\'annonce a été supprimé avec succès.', 'success');
			}
		} catch (err) {
			console.error('Error deleting post type:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de la suppression du type d\'annonce. Il est peut-être utilisé par des annonces existantes.';
			swal('Erreur', errorMsg, 'error');
		}
	};

	const handleDeleteSelected = async () => {
		const willDelete = await swal({
			title: 'Êtes-vous sûr ?',
			text: `Vous allez supprimer ${selectedPostTypes.length} type(s) d'annonce. Cette action est irréversible !`,
			icon: 'warning',
			buttons: {
				cancel: {
					text: 'Annuler',
					value: false,
					visible: true,
					className: 'btn-secondary'
				},
				confirm: {
					text: 'Oui, supprimer tout',
					value: true,
					visible: true,
					className: 'btn-danger'
				}
			},
			dangerMode: true,
		});

		if (!willDelete) return;
		
		try {
			let successCount = 0;
			let errorCount = 0;
			
			for (const id of selectedPostTypes) {
				try {
					await axiosClient.delete(`/admin/postTypes/${id}`, {
						headers: { 
							'Accept': 'application/json',
							...getAuthHeader() 
						},
					});
					successCount++;
				} catch (err) {
					errorCount++;
					console.error(`Error deleting post type ${id}:`, err);
				}
			}
			
			await fetchPostTypes();
			setSelectedPostTypes([]);
			
			if (errorCount === 0) {
				swal('Supprimé !', `${successCount} type(s) d'annonce supprimé(s) avec succès.`, 'success');
			} else {
				swal('Attention', `${successCount} supprimé(s), ${errorCount} échec(s). Certains types sont peut-être utilisés par des annonces.`, 'warning');
			}
		} catch (err) {
			console.error('Error deleting post types:', err);
			swal('Erreur', 'Erreur lors de la suppression des types d\'annonce', 'error');
		}
	};

	const toggleActive = async (postType) => {
		try {
			const response = await axiosClient.put(
				`/admin/postTypes/${postType.id}/toggle-active`,
				{},
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200) {
				await fetchPostTypes();
			}
		} catch (err) {
			console.error('Error toggling active status:', err);
		}
	};

	return (
		<>
			{/* Header */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex justify-content-between align-items-center flex-wrap">
						<div>
							<h2 className="mb-1" style={{ fontSize: '28px', fontWeight: '700' }}>Types D'annonce</h2>
							<p className="text-muted mb-0">
								Affichage des éléments 1 à {filteredPostTypes.length} sur {filteredPostTypes.length}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Help Section */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="alert alert-info" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#e7f3ff' }}>
						<div className="d-flex align-items-start">
							<i className="fas fa-info-circle me-3 mt-1" style={{ fontSize: '20px', color: '#0066cc' }}></i>
							<div>
								<strong>Help</strong>
								<p className="mb-0 mt-2" style={{ fontSize: '14px', lineHeight: '1.6' }}>
									To translate this list you have to select a language by clicking on the dropdown button <span className="badge bg-primary">🌐</span> icon located at the right of each <span className="badge bg-success">✏ Edit</span> button. Translatable fields can be identified by the <span className="badge bg-secondary">🌐</span> icon before or after their label in the form.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Action Buttons */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex gap-2 flex-wrap">
						<button 
							className="btn btn-primary btn-sm"
							onClick={() => setShowAddModal(true)}
							style={{ borderRadius: '8px', fontWeight: '600', width: '130px' }}
						>
							<i className="fas fa-plus me-2"></i>Ajouter
						</button>
						<button 
							className="btn btn-danger btn-sm"
							disabled={selectedPostTypes.length === 0}
							onClick={handleDeleteSelected}
							style={{ borderRadius: '8px', fontWeight: '600', width: '130px' }}
						>
							<i className="fas fa-times me-2"></i>Supprimer
						</button>
					</div>
				</div>
			</div>

			{/* Records per page and Search */}
			<div className="row mb-3">
				<div className="col-md-6">
					<div className="d-flex align-items-center">
						<select 
							className="form-select" 
							style={{ width: '100px', borderRadius: '8px' }}
							value={recordsPerPage}
							onChange={(e) => setRecordsPerPage(Number(e.target.value))}
						>
							<option value={25}>25</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
						</select>
						<span className="ms-3 text-muted">records per page</span>
					</div>
				</div>
				<div className="col-md-6">
					<div className="d-flex justify-content-end">
						<div className="input-group" style={{ maxWidth: '300px' }}>
							<span className="input-group-text bg-white" style={{ borderRadius: '8px 0 0 8px' }}>
								Search:
							</span>
							<input 
								type="text" 
								className="form-control" 
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								style={{ borderRadius: '0 8px 8px 0' }}
								placeholder="Search categories..."
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Table */}
			<div className="row">
				<div className="col-12">
					<div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
						<div className="card-body p-0">
							{loading ? (
								<div className="text-center py-5">
									<div className="spinner-border text-primary" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
								</div>
							) : filteredPostTypes.length === 0 ? (
								<div className="text-center py-5">
									<p className="text-muted">Aucun type d'annonce trouvé</p>
								</div>
							) : (
								<div className="table-responsive">
									<table className="table table-hover mb-0">
										<thead style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
											<tr>
												<th style={{ padding: '16px', width: '50px' }}>
													<input 
														type="checkbox" 
														className="form-check-input"
														onChange={(e) => {
															if (e.target.checked) {
																setSelectedPostTypes(filteredPostTypes.map(pt => pt.id));
															} else {
																setSelectedPostTypes([]);
															}
														}}
														checked={selectedPostTypes.length === filteredPostTypes.length && filteredPostTypes.length > 0}
													/>
												</th>
												<th style={{ padding: '16px', fontWeight: '600', fontSize: '14px' }}>
													<div className="d-flex align-items-center">
														Name
														<i className="fas fa-sort ms-2 text-muted" style={{ fontSize: '12px', cursor: 'pointer' }}></i>
													</div>
												</th>
												<th style={{ padding: '16px', fontWeight: '600', fontSize: '14px', textAlign: 'center' }}>
													<div className="d-flex align-items-center justify-content-center">
														Active
														<i className="fas fa-sort ms-2 text-muted" style={{ fontSize: '12px', cursor: 'pointer' }}></i>
													</div>
												</th>
												<th style={{ padding: '16px', fontWeight: '600', fontSize: '14px', textAlign: 'center' }}>
													Actions
												</th>
											</tr>
										</thead>
										<tbody>
											{filteredPostTypes.map((postType) => (
												<tr 
													key={postType.id}
													style={{ 
														borderBottom: '1px solid #f0f0f0',
														transition: 'background-color 0.2s'
													}}
													onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
													onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
												>
													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
														<input 
															type="checkbox" 
															className="form-check-input"
															checked={selectedPostTypes.includes(postType.id)}
															onChange={() => {
																if (selectedPostTypes.includes(postType.id)) {
																	setSelectedPostTypes(selectedPostTypes.filter(id => id !== postType.id));
																} else {
																	setSelectedPostTypes([...selectedPostTypes, postType.id]);
																}
															}}
														/>
													</td>
													<td style={{ padding: '16px', verticalAlign: 'middle' }}>
														<span style={{ fontSize: '15px', fontWeight: '500' }}>
															{postType.name}
														</span>
													</td>
													<td style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
														<div 
															onClick={() => toggleActive(postType)}
															style={{
																display: 'inline-flex',
																alignItems: 'center',
																justifyContent: postType.active === 1 ? 'flex-end' : 'flex-start',
																width: '50px',
																height: '26px',
																backgroundColor: postType.active === 1 ? '#28a745' : '#dc3545',
																borderRadius: '13px',
																padding: '2px',
																cursor: 'pointer',
																transition: 'all 0.3s ease',
																boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
															}}
														>
															<div 
																style={{
																	width: '22px',
																	height: '22px',
																	backgroundColor: 'white',
																	borderRadius: '50%',
																	transition: 'all 0.3s ease',
																	boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
																}}
															/>
														</div>
													</td>
													<td style={{ padding: '16px', verticalAlign: 'middle', textAlign: 'center' }}>
														<div className="d-flex gap-2 justify-content-center">
															<button 
																className="btn btn-sm btn-primary"
																onClick={() => handleEditPostType(postType)}
																style={{ borderRadius: '6px', padding: '6px 12px' }}
															>
																<i className="fas fa-edit me-1"></i>Edit
															</button>
															<button 
																className="btn btn-sm btn-danger"
																onClick={() => handleDeletePostType(postType.id)}
																style={{ borderRadius: '6px', padding: '6px 12px' }}
															>
																<i className="fas fa-trash me-1"></i>Delete
															</button>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Footer Info */}
			<div className="row mt-3">
				<div className="col-12">
					<div className="d-flex justify-content-between align-items-center">
						<p className="text-muted mb-0" style={{ fontSize: '14px' }}>
							Showing entries 1 to {filteredPostTypes.length} of {filteredPostTypes.length}.
						</p>
						<div className="d-flex gap-2">
							<button className="btn btn-sm btn-outline-primary" disabled>Previous</button>
							<button className="btn btn-sm btn-primary">1</button>
							<button className="btn btn-sm btn-outline-primary" disabled>Next</button>
						</div>
					</div>
				</div>
			</div>

			{/* Add Modal */}
			<Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
				<Modal.Header closeButton style={{ borderBottom: '2px solid #f0f0f0' }}>
					<Modal.Title style={{ fontWeight: '700' }}>Ajouter un type d'annonce</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<label className="form-label fw-bold">Nom *</label>
						<input
							type="text"
							className="form-control"
							value={formData.name}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							placeholder="Ex: Temps plein"
							style={{ borderRadius: '8px' }}
						/>
					</div>
					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							checked={formData.active === 1}
							onChange={(e) => setFormData({ ...formData, active: e.target.checked ? 1 : 0 })}
							id="addActiveCheck"
							style={{ width: '48px', height: '24px' }}
						/>
						<label className="form-check-label fw-bold ms-2" htmlFor="addActiveCheck">
							Active
						</label>
					</div>
				</Modal.Body>
				<Modal.Footer style={{ borderTop: '2px solid #f0f0f0' }}>
					<button className="btn btn-secondary" onClick={() => setShowAddModal(false)} style={{ borderRadius: '8px' }}>
						Annuler
					</button>
					<button
						className="btn btn-primary"
						onClick={handleAddPostType}
						disabled={!formData.name}
						style={{ borderRadius: '8px' }}
					>
						<i className="fas fa-save me-2"></i>Ajouter
					</button>
				</Modal.Footer>
			</Modal>

			{/* Edit Modal */}
			<Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
				<Modal.Header closeButton style={{ borderBottom: '2px solid #f0f0f0' }}>
					<Modal.Title style={{ fontWeight: '700' }}>Modifier le type d'annonce</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mb-3">
						<label className="form-label fw-bold">Nom *</label>
						<input
							type="text"
							className="form-control"
							value={formData.name}
							onChange={(e) => setFormData({ ...formData, name: e.target.value })}
							style={{ borderRadius: '8px' }}
						/>
					</div>
					<div className="form-check form-switch">
						<input
							className="form-check-input"
							type="checkbox"
							checked={formData.active === 1}
							onChange={(e) => setFormData({ ...formData, active: e.target.checked ? 1 : 0 })}
							id="editActiveCheck"
							style={{ width: '48px', height: '24px' }}
						/>
						<label className="form-check-label fw-bold ms-2" htmlFor="editActiveCheck">
							Active
						</label>
					</div>
				</Modal.Body>
				<Modal.Footer style={{ borderTop: '2px solid #f0f0f0' }}>
					<button className="btn btn-secondary" onClick={() => setShowEditModal(false)} style={{ borderRadius: '8px' }}>
						Annuler
					</button>
					<button
						className="btn btn-primary"
						onClick={handleUpdatePostType}
						disabled={!formData.name}
						style={{ borderRadius: '8px' }}
					>
						<i className="fas fa-save me-2"></i>Mettre à jour
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default JobTypes;
